// frontend/src/portal/pages/PortalOverview.jsx
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { fetchPortalCompanies, fetchSuperAdminTenants } from "../../api.js";
import { usePortalAuth } from "../components/RequireClient.jsx";
import ErrorBoundary from "../../components/ErrorBoundary.jsx";
import * as Sentry from "@sentry/react";
import {
  BarChart3,
  Wrench,
  FileText,
  Building2,
  Users,
  Mail,
  Palette,
  FolderOpen,
  Rocket,
  Search,
  ChevronDown,
  Crown,
  Star,
  Briefcase,
  User,
  Clock,
  AlertCircle,
  CheckCircle2,
  Shield,
} from "lucide-react";
import { getTenantWorkspaceUrl } from "../tenantWorkspaceUrl.js";

export default function PortalOverview() {
  const { memberRole, tenant, tenantSlug, me } = usePortalAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTenantPicker, setShowTenantPicker] = useState(false);
  const [availableTenants, setAvailableTenants] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [tenantSearchTerm, setTenantSearchTerm] = useState("");
  const [showCompanies, setShowCompanies] = useState(false);
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [riskFilter, setRiskFilter] = useState([]);
  const [onlyNeedsAttention, setOnlyNeedsAttention] = useState(false);
  const [onlyMissingRequired, setOnlyMissingRequired] = useState(false);
  const [onlyExpiringSoon, setOnlyExpiringSoon] = useState(false);
  const [onlyPendingItems, setOnlyPendingItems] = useState(false);
  const [onlyReviewDue, setOnlyReviewDue] = useState(false);
  const [sortMode, setSortMode] = useState("needs_attention");

  const isSuperAdmin = memberRole === "super_admin";
  const isTenantAdmin = ["owner", "admin"].includes(memberRole);
  const isStaff = ["staff", "reviewer"].includes(memberRole);
  const isClientUser = memberRole === "client_user";
  const canManageTenant = isSuperAdmin || isTenantAdmin;
  const canClickCompanies = !isClientUser;
  const tenantName = tenant?.name || tenantSlug || "your organization";

  const displayName = useMemo(() => {
    if (me?.firstName || me?.lastName) {
      return `${me.firstName || ""} ${me.lastName || ""}`.trim();
    }
    if (me?.email) {
      return me.email.split("@")[0];
    }
    return null;
  }, [me]);

  const tenantAppUrl = useMemo(() => {
    return getTenantWorkspaceUrl(tenantSlug);
  }, [tenantSlug]);

  const tenantLogoUrl = tenant?.logo_url || null;
  const tenantBrandName = tenant?.name || "CBCFX";

  const formatRelative = (dateLike) => {
    if (!dateLike) return null;
    const t = new Date(dateLike).getTime();
    if (!Number.isFinite(t)) return null;
    const diffMs = Date.now() - t;
    const min = Math.floor(diffMs / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min}m ago`;
    const hrs = Math.floor(min / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    const years = Math.floor(months / 12);
    return `${years}y ago`;
  };

  const riskChip = (riskCode) => {
    const r = String(riskCode || "").toUpperCase();
    if (!r) return null;
    const map = {
      VH: { bg: "rgba(239,68,68,0.15)", text: "var(--danger)" },
      H: { bg: "rgba(245,158,11,0.15)", text: "var(--warning)" },
      M: { bg: "rgba(6,182,212,0.15)", text: "var(--info)" },
      L: { bg: "rgba(16,185,129,0.15)", text: "var(--success)" },
    };
    const c = map[r] || { bg: "var(--surface-elevated)", text: "var(--text-muted)" };
    return { label: `Risk ${r}`, ...c };
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await fetchPortalCompanies();
        if (mounted) setCompanies(rows || []);
      } catch (err) {
        if (mounted) setError(err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => (mounted = false);
  }, []);

  const handleOpenTenantPicker = async () => {
    setShowTenantPicker(true);
    setLoadingTenants(true);
    try {
      const data = await fetchSuperAdminTenants({ limit: 1000 });
      setAvailableTenants(data.tenants || []);
    } catch (err) {
      console.error("Failed to load tenants:", err);
      setAvailableTenants([]);
    } finally {
      setLoadingTenants(false);
    }
  };

  const handleSelectTenant = (selectedTenant) => {
    const slug = selectedTenant.slug;

    if (!slug) {
      alert("This tenant doesn't have a slug configured yet.");
      return;
    }

    const targetUrl = getTenantWorkspaceUrl(slug);

    if (!targetUrl) {
      alert("Unable to determine this tenant's application URL.");
      return;
    }

    window.location.href = targetUrl;
  };

  const filteredTenants = useMemo(() => {
    if (!tenantSearchTerm) return availableTenants;
    const term = tenantSearchTerm.toLowerCase();
    return availableTenants.filter((t) => t.name?.toLowerCase().includes(term) || t.slug?.toLowerCase().includes(term));
  }, [availableTenants, tenantSearchTerm]);

  const filteredCompanies = useMemo(() => {
    const term = (companySearchTerm || "").trim().toLowerCase();

    let list = Array.isArray(companies) ? companies.slice() : [];

    if (term) {
      list = list.filter((c) => {
        const name = String(c.name || "").toLowerCase();
        const reg = String(c.registration_no || "").toLowerCase();
        return name.includes(term) || reg.includes(term);
      });
    }

    if (statusFilter.length) {
      const set = new Set(statusFilter.map((s) => String(s).toLowerCase()));
      list = list.filter((c) => set.has(String(c.status || "").toLowerCase()));
    }

    if (riskFilter.length) {
      const set = new Set(riskFilter.map((r) => String(r).toLowerCase()));
      list = list.filter((c) => set.has(String(c.risk_code || "").toLowerCase()));
    }

    if (onlyNeedsAttention) {
      list = list.filter((c) => Number(c.needs_attention_count || 0) > 0);
    }
    if (onlyMissingRequired) {
      list = list.filter((c) => Number(c.missing_required_count || 0) > 0);
    }
    if (onlyExpiringSoon) {
      list = list.filter((c) => Number(c.expiring_soon_count || 0) > 0);
    }
    if (onlyPendingItems) {
      list = list.filter((c) => Number(c.pending_items_count || 0) > 0);
    }
    if (onlyReviewDue) {
      list = list.filter((c) => !!c.review_due);
    }

    list.sort((a, b) => {
      if (sortMode === "name") {
        return String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" });
      }
      if (sortMode === "last_activity") {
        const ta = new Date(a.last_activity_at || a.updated_at || a.created_at || 0).getTime() || 0;
        const tb = new Date(b.last_activity_at || b.updated_at || b.created_at || 0).getTime() || 0;
        return tb - ta;
      }

      const na = Number(a.needs_attention_count || 0);
      const nb = Number(b.needs_attention_count || 0);
      if (nb !== na) return nb - na;

      const ta = new Date(a.last_activity_at || a.updated_at || a.created_at || 0).getTime() || 0;
      const tb = new Date(b.last_activity_at || b.updated_at || b.created_at || 0).getTime() || 0;
      return ta - tb;
    });

    return list;
  }, [companies, companySearchTerm, statusFilter, riskFilter, onlyNeedsAttention, onlyMissingRequired, onlyExpiringSoon, onlyPendingItems, onlyReviewDue, sortMode]);

  const getRoleBadgeColor = () => {
    if (isSuperAdmin) return { bg: "var(--info-bg)", text: "var(--info)", icon: Star };
    if (isTenantAdmin) return { bg: "var(--warning-bg)", text: "var(--warning)", icon: Crown };
    if (isStaff) return { bg: "var(--accent-primary)", color: "#fff", icon: Briefcase };
    return { bg: "var(--surface-elevated)", text: "var(--text-muted)", icon: User };
  };

  const roleBadge = getRoleBadgeColor();
  const RoleIcon = roleBadge.icon;

  return (
    <ErrorBoundary componentName="PortalOverview">
      <div style={{ minHeight: "100vh", background: "var(--bg-secondary)" }}>
        {/* HERO SECTION - 20% SMALLER */}
        <div
          style={{
            background: "var(--accent-gradient)",
            borderBottom: "1px solid var(--border-secondary)",
          }}>
          <div
            style={{
              maxWidth: "var(--container-max-wide)",
              margin: "0 auto",
              padding: "38px 32px 51px", // Reduced from 48px/64px (20% smaller)
            }}>
            <div
              style={{
                marginBottom: "32px", // Reduced from 40px
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "20px", // Reduced from 24px
              }}>
              {tenantLogoUrl ? (
                <img
                  src={tenantLogoUrl}
                  alt={tenantBrandName}
                  style={{
                    maxWidth: "336px", // Reduced from 420px (20% smaller)
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.4))",
                  }}
                />
              ) : (
                <img
                  src="/cbcfx-logo.svg"
                  alt="CBCFX"
                  style={{
                    maxWidth: "336px", // Reduced from 420px (20% smaller)
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.4))",
                  }}
                />
              )}

              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    fontSize: 13,
                    textTransform: "uppercase",
                    letterSpacing: "0.14em",
                    color: "rgba(255,255,255,0.75)",
                    marginBottom: 10,
                    fontWeight: 500,
                  }}>
                  Client Portal
                </div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 600,
                    color: "#ffffff",
                  }}>
                  {tenantBrandName}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              {" "}
              {/* Reduced from 24px */}
              <h1
                style={{
                  fontSize: "36px",
                  fontWeight: "400",
                  color: "#fff",
                  margin: "0 0 8px 0",
                  letterSpacing: "-0.5px",
                }}>
                Welcome back{displayName ? `, ${displayName}` : ""}
              </h1>
              <p
                style={{
                  fontSize: "16px",
                  color: "rgba(255,255,255,0.9)",
                  margin: 0,
                }}>
                You have access to {companies.length} {companies.length === 1 ? "company" : "companies"}
              </p>
            </div>

            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "6px 14px",
                borderRadius: "var(--radius-md)",
                backgroundColor: "rgba(255,255,255,0.2)",
                backdropFilter: "blur(10px)",
                color: "#fff",
                fontSize: "13px",
                fontWeight: "600",
                marginBottom: "24px", // Reduced from 32px
                border: "1px solid rgba(255,255,255,0.3)",
              }}>
              <RoleIcon size={16} />
              <span>{memberRole.replace("_", " ").toUpperCase()}</span>
            </div>

            {/* IMPROVED BUTTON VISIBILITY */}
            {tenantAppUrl && (
              <div>
                <button
                  onClick={() => (window.location.href = tenantAppUrl)}
                  style={{
                    padding: "12px 28px",
                    fontSize: "15px",
                    fontWeight: "600",
                    color: "#fff",
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                    border: "2px solid rgba(255, 255, 255, 0.8)",
                    borderRadius: "var(--radius-md)",
                    cursor: "pointer",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
                    transition: "all var(--transition-fast) var(--ease-out-quart)",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    backdropFilter: "blur(10px)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.25)";
                    e.currentTarget.style.borderColor = "#fff";
                    e.currentTarget.style.boxShadow = "0 12px 32px rgba(0, 0, 0, 0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.15)";
                    e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.8)";
                    e.currentTarget.style.boxShadow = "0 8px 24px rgba(0, 0, 0, 0.2)";
                  }}>
                  <Rocket size={18} />
                  Open CBCFX App
                </button>
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            maxWidth: "var(--container-max-wide)",
            margin: "0 auto",
            padding: "var(--space-2xl) var(--space-xl)",
          }}>
          <div style={{ marginBottom: "48px" }}>
            <h2
              style={{
                fontSize: "20px",
                fontWeight: "600",
                color: "var(--text-primary)",
                marginBottom: "var(--space-xl)",
              }}>
              Quick Actions
            </h2>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: "var(--space-lg)",
              }}>
              {isSuperAdmin && (
                <>
                  <ActionCard
                    icon={BarChart3}
                    title="Super Admin Dashboard"
                    description="View system overview and statistics"
                    onClick={() => navigate("/portal/admin/dashboard")}
                  />
                  <ActionCard icon={Wrench} title="Manage Tenants" description="Configure tenant settings" onClick={() => navigate("/portal/admin/tenants")} />
                  <ActionCard icon={FileText} title="Audit Log" description="Review system activity" onClick={() => navigate("/portal/ops/audit")} />
                  <ActionCard icon={Building2} title="Access Tenant Data" description="Switch to any tenant's app" onClick={handleOpenTenantPicker} />

                  {tenantSlug && (
                    <>
                      <ActionCard icon={Users} title="Manage Members" description="Add or update team members" onClick={() => navigate("/portal/admin/members")} />
                      <ActionCard icon={Mail} title="Invite Users" description="Send new invitations" onClick={() => navigate("/portal/admin/invite")} />
                      <ActionCard icon={Palette} title="Branding & Logo" description="Upload your logo for the CBCFX portal" onClick={() => navigate("/portal/admin/branding")} />
                    </>
                  )}
                </>
              )}

              {canManageTenant && !isSuperAdmin && (
                <>
                  <ActionCard icon={Users} title="Manage Members" description="Add or update team members" onClick={() => navigate("/portal/admin/members")} />
                  <ActionCard icon={Mail} title="Invite Users" description="Send new invitations" onClick={() => navigate("/portal/admin/invite")} />
                  <ActionCard icon={Palette} title="Branding & Logo" description="Upload your logo for the CBCFX portal" onClick={() => navigate("/portal/admin/branding")} />
                </>
              )}
            </div>
          </div>

          <div
            style={{
              backgroundColor: "var(--surface)",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--border-primary)",
              overflow: "hidden",
              boxShadow: "var(--shadow-sm)",
            }}>
            <div
              onClick={() => setShowCompanies(!showCompanies)}
              style={{
                padding: "var(--space-xl)",
                borderBottom: showCompanies ? "1px solid var(--border-primary)" : "none",
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                transition: "background-color var(--transition-fast)",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--surface-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--space-md)",
                }}>
                <FolderOpen size={24} style={{ color: "var(--accent-primary)" }} />
                <h2
                  style={{
                    fontSize: "20px",
                    fontWeight: "600",
                    color: "var(--text-primary)",
                    margin: 0,
                  }}>
                  Companies
                </h2>
                <span
                  style={{
                    fontSize: "13px",
                    color: "var(--text-muted)",
                    fontWeight: "500",
                    backgroundColor: "var(--surface-elevated)",
                    padding: "2px 8px",
                    borderRadius: "var(--radius-sm)",
                  }}>
                  {companies.length}
                </span>
              </div>
              <ChevronDown
                size={20}
                style={{
                  color: "var(--text-muted)",
                  transition: "transform var(--transition-fast)",
                  transform: showCompanies ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </div>

            {showCompanies && (
              <div style={{ padding: "var(--space-xl)" }}>
                {loading && (
                  <div style={{ textAlign: "center", padding: "48px" }}>
                    <div
                      style={{
                        width: "32px",
                        height: "32px",
                        border: "3px solid var(--border-primary)",
                        borderTopColor: "var(--accent-primary)",
                        borderRadius: "50%",
                        animation: "spin 0.8s linear infinite",
                        margin: "0 auto 16px",
                      }}
                    />
                    <p style={{ color: "var(--text-muted)" }}>Loading companies...</p>
                  </div>
                )}

                {error && (
                  <div style={{ textAlign: "center", padding: "48px" }}>
                    <AlertCircle size={48} style={{ color: "var(--danger)", marginBottom: "16px" }} />
                    <p
                      style={{
                        color: "var(--danger)",
                        marginTop: "16px",
                      }}>
                      {error.message || "Unable to load companies."}
                    </p>
                  </div>
                )}

                {!loading && !error && (
                  <>
                    {/* MORE PROMINENT SEARCH BAR */}
                    {companies.length > 0 && (
                      <div style={{ position: "relative", marginBottom: "var(--space-xl)" }}>
                        <Search
                          size={20}
                          style={{
                            position: "absolute",
                            left: "16px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: "var(--accent-primary)",
                            pointerEvents: "none",
                          }}
                        />
                        <input
                          type="text"
                          placeholder="Search companies by name or registration number..."
                          value={companySearchTerm}
                          onChange={(e) => setCompanySearchTerm(e.target.value)}
                          style={{
                            width: "100%",
                            padding: "14px 16px 14px 48px",
                            border: "2px solid var(--border-primary)",
                            borderRadius: "var(--radius-md)",
                            fontSize: "15px",
                            outline: "none",
                            transition: "all var(--transition-fast)",
                            backgroundColor: "var(--surface)",
                            color: "var(--text-primary)",
                            boxShadow: "var(--shadow-sm)",
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = "var(--accent-primary)";
                            e.target.style.boxShadow = "0 0 0 3px rgba(37, 99, 235, 0.1)";
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = "var(--border-primary)";
                            e.target.style.boxShadow = "var(--shadow-sm)";
                          }}
                        />
                      </div>
                    )}
                    {companies.length > 0 && (
                      <div className="portal-filters" style={{ marginBottom: "var(--space-xl)" }}>
                        <div className="portal-filters-row">
                          <div className="portal-filter">
                            <label className="portal-filter-label">Status</label>
                            <select
                              multiple
                              value={statusFilter}
                              onChange={(e) => {
                                const vals = Array.from(e.target.selectedOptions).map((o) => o.value);
                                setStatusFilter(vals);
                              }}
                              className="portal-filter-select"
                              aria-label="Filter by status">
                              {Array.from(new Set((companies || []).map((c) => String(c.status || "").toLowerCase()).filter(Boolean)))
                                .sort()
                                .map((s) => (
                                  <option key={s} value={s}>
                                    {s}
                                  </option>
                                ))}
                            </select>
                          </div>

                          <div className="portal-filter">
                            <label className="portal-filter-label">Risk</label>
                            <select
                              multiple
                              value={riskFilter}
                              onChange={(e) => {
                                const vals = Array.from(e.target.selectedOptions).map((o) => o.value);
                                setRiskFilter(vals);
                              }}
                              className="portal-filter-select"
                              aria-label="Filter by risk">
                              {Array.from(new Set((companies || []).map((c) => String(c.risk_code || "").toUpperCase()).filter(Boolean)))
                                .sort()
                                .map((r) => (
                                  <option key={r} value={r}>
                                    {r}
                                  </option>
                                ))}
                            </select>
                          </div>

                          <div className="portal-filter">
                            <label className="portal-filter-label">Sort</label>
                            <select value={sortMode} onChange={(e) => setSortMode(e.target.value)} className="portal-filter-select">
                              <option value="needs_attention">Needs attention</option>
                              <option value="last_activity">Last activity</option>
                              <option value="name">Name</option>
                            </select>
                          </div>
                        </div>

                        <div className="portal-filters-toggles">
                          <label className="portal-toggle">
                            <input type="checkbox" checked={onlyNeedsAttention} onChange={(e) => setOnlyNeedsAttention(e.target.checked)} />
                            Needs attention
                          </label>
                          <label className="portal-toggle">
                            <input type="checkbox" checked={onlyMissingRequired} onChange={(e) => setOnlyMissingRequired(e.target.checked)} />
                            Missing required docs
                          </label>
                          <label className="portal-toggle">
                            <input type="checkbox" checked={onlyPendingItems} onChange={(e) => setOnlyPendingItems(e.target.checked)} />
                            Pending
                          </label>
                          <label className="portal-toggle">
                            <input type="checkbox" checked={onlyExpiringSoon} onChange={(e) => setOnlyExpiringSoon(e.target.checked)} />
                            Expiring soon
                          </label>
                          <label className="portal-toggle">
                            <input type="checkbox" checked={onlyReviewDue} onChange={(e) => setOnlyReviewDue(e.target.checked)} />
                            Review due
                          </label>

                          <button
                            type="button"
                            className="portal-filter-clear"
                            onClick={() => {
                              setStatusFilter([]);
                              setRiskFilter([]);
                              setOnlyNeedsAttention(false);
                              setOnlyMissingRequired(false);
                              setOnlyPendingItems(false);
                              setOnlyExpiringSoon(false);
                              setOnlyReviewDue(false);
                              setSortMode("needs_attention");
                            }}>
                            Clear
                          </button>
                        </div>
                      </div>
                    )}

                    {filteredCompanies.length === 0 ? (
                      <div style={{ textAlign: "center", padding: "48px" }}>
                        <Search size={48} style={{ color: "var(--text-disabled)", opacity: 0.3, marginBottom: "16px" }} />
                        <p
                          style={{
                            color: "var(--text-muted)",
                            marginTop: "16px",
                          }}>
                          {companySearchTerm ? "No companies match your search." : "No companies available."}
                        </p>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                          gap: "var(--space-lg)",
                          maxHeight: "600px",
                          overflowY: "auto",
                          padding: "var(--space-xs)",
                        }}>
                        {filteredCompanies.map((company) => {
                          const CardComponent = canClickCompanies ? Link : "div";
                          const statusColors = {
                            active: {
                              bg: "var(--success-bg)",
                              text: "var(--success)",
                            },
                            pending: {
                              bg: "var(--warning-bg)",
                              text: "var(--warning)",
                            },
                            inactive: {
                              bg: "var(--danger-bg)",
                              text: "var(--danger)",
                            },
                          };
                          const statusColor = statusColors[company.status] || {
                            bg: "var(--surface-elevated)",
                            text: "var(--text-muted)",
                          };

                          return (
                            <CardComponent
                              key={company.id}
                              to={canClickCompanies ? `/portal/company/${company.id}` : undefined}
                              style={{
                                backgroundColor: canClickCompanies ? "var(--surface)" : "var(--surface-elevated)",
                                border: "1px solid var(--border-primary)",
                                borderRadius: "var(--radius-md)",
                                padding: "var(--space-lg)",
                                cursor: canClickCompanies ? "pointer" : "default",
                                textDecoration: "none",
                                color: "inherit",
                                display: "block",
                                transition: "all var(--transition-fast) var(--ease-out-quart)",
                                opacity: company.is_archived ? 0.7 : 1,
                              }}
                              onMouseEnter={(e) => {
                                if (canClickCompanies) {
                                  e.currentTarget.style.boxShadow = "var(--shadow-md)";
                                  e.currentTarget.style.transform = "translateY(-2px)";
                                  e.currentTarget.style.borderColor = "var(--border-accent)";
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = "none";
                                e.currentTarget.style.transform = "none";
                                e.currentTarget.style.borderColor = "var(--border-primary)";
                              }}>
                              <div style={{ display: "flex", justifyContent: "space-between", gap: "12px" }}>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                                    <h4
                                      style={{
                                        margin: 0,
                                        fontSize: "16px",
                                        fontWeight: "600",
                                        color: "var(--text-primary)",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        maxWidth: "520px",
                                      }}>
                                      {company.name}
                                    </h4>

                                    <span
                                      style={{
                                        padding: "4px 10px",
                                        borderRadius: "var(--radius-sm)",
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        backgroundColor: statusColor.bg,
                                        color: statusColor.text,
                                        textTransform: "capitalize",
                                      }}>
                                      {company.status}
                                    </span>

                                    {riskChip(company.risk_code) && (
                                      <span
                                        className="signal-chip"
                                        style={{
                                          background: riskChip(company.risk_code).bg,
                                          color: riskChip(company.risk_code).text,
                                        }}>
                                        {riskChip(company.risk_code).label}
                                      </span>
                                    )}
                                  </div>

                                  <div style={{ marginTop: "10px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                                    {Number(company.missing_required_count || 0) > 0 && (
                                      <span className="signal-chip signal-chip-danger">
                                        <AlertCircle size={12} style={{ marginRight: "4px" }} />
                                        Missing docs {company.missing_required_count}
                                      </span>
                                    )}
                                    {Number(company.pending_items_count || 0) > 0 && (
                                      <span className="signal-chip signal-chip-warn">
                                        <Clock size={12} style={{ marginRight: "4px" }} />
                                        Pending {company.pending_items_count}
                                      </span>
                                    )}
                                    {Number(company.expiring_soon_count || 0) > 0 && (
                                      <span className="signal-chip signal-chip-warn">
                                        <AlertCircle size={12} style={{ marginRight: "4px" }} />
                                        Expiring soon {company.expiring_soon_count}
                                      </span>
                                    )}
                                    {!!company.review_due && (
                                      <span className="signal-chip signal-chip-info">
                                        <Shield size={12} style={{ marginRight: "4px" }} />
                                        Review due
                                      </span>
                                    )}

                                    {Number(company.needs_attention_count || 0) === 0 && (
                                      <span className="signal-chip signal-chip-ok">
                                        <CheckCircle2 size={12} style={{ marginRight: "4px" }} />
                                        All good
                                      </span>
                                    )}
                                  </div>

                                  {/* ICONS IN COMPANY METADATA */}
                                  <div
                                    style={{
                                      marginTop: "10px",
                                      fontSize: "13px",
                                      color: "var(--text-secondary)",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "12px",
                                      flexWrap: "wrap",
                                    }}>
                                    {company.registration_no && (
                                      <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                        <Building2 size={14} style={{ color: "var(--text-muted)" }} />
                                        {company.registration_no}
                                      </span>
                                    )}
                                    {company.last_activity_at && (
                                      <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                        <Clock size={14} style={{ color: "var(--text-muted)" }} />
                                        Updated {formatRelative(company.last_activity_at)}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {isSuperAdmin && (
                                  <div
                                    style={{
                                      fontSize: "12px",
                                      color: "var(--text-muted)",
                                      textAlign: "right",
                                      whiteSpace: "nowrap",
                                    }}>
                                    <div style={{ fontWeight: 600, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: "4px", justifyContent: "flex-end" }}>
                                      <Building2 size={14} style={{ color: "var(--text-muted)" }} />
                                      {company.tenant_name}
                                    </div>
                                    <div style={{ marginTop: "4px" }}>{company.tenant_slug}</div>
                                  </div>
                                )}
                              </div>
                            </CardComponent>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {showTenantPicker && (
          <div
            onClick={() => setShowTenantPicker(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "var(--space-xl)",
            }}>
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: "var(--surface)",
                borderRadius: "var(--radius-lg)",
                maxWidth: "700px",
                width: "100%",
                maxHeight: "85vh",
                display: "flex",
                flexDirection: "column",
                boxShadow: "var(--shadow-xl)",
                border: "1px solid var(--border-primary)",
              }}>
              <div
                style={{
                  padding: "var(--space-xl)",
                  borderBottom: "1px solid var(--border-primary)",
                }}>
                <h2
                  style={{
                    fontSize: "24px",
                    fontWeight: "600",
                    color: "var(--text-primary)",
                    margin: "0 0 var(--space-sm) 0",
                  }}>
                  Select Tenant to Access
                </h2>
                <p
                  style={{
                    fontSize: "14px",
                    color: "var(--text-muted)",
                    margin: 0,
                  }}>
                  Choose a tenant to access their main CBCFX application
                </p>
              </div>

              <div
                style={{
                  padding: "var(--space-xl)",
                  overflowY: "auto",
                  flex: 1,
                }}>
                <div style={{ position: "relative", marginBottom: "var(--space-lg)" }}>
                  <Search
                    size={18}
                    style={{
                      position: "absolute",
                      left: "var(--space-lg)",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "var(--text-disabled)",
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Search tenants by name or slug..."
                    value={tenantSearchTerm}
                    onChange={(e) => setTenantSearchTerm(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "var(--space-md) var(--space-lg) var(--space-md) 48px",
                      border: "1px solid var(--border-primary)",
                      borderRadius: "var(--radius-md)",
                      fontSize: "14px",
                      outline: "none",
                      backgroundColor: "var(--surface)",
                      color: "var(--text-primary)",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "var(--border-accent)")}
                    onBlur={(e) => (e.target.style.borderColor = "var(--border-primary)")}
                  />
                </div>

                {loadingTenants ? (
                  <div style={{ textAlign: "center", padding: "48px" }}>
                    <div
                      style={{
                        width: "32px",
                        height: "32px",
                        border: "3px solid var(--border-primary)",
                        borderTopColor: "var(--accent-primary)",
                        borderRadius: "50%",
                        animation: "spin 0.8s linear infinite",
                        margin: "0 auto 16px",
                      }}
                    />
                    <p style={{ color: "var(--text-muted)" }}>Loading tenants...</p>
                  </div>
                ) : filteredTenants.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "48px" }}>
                    <Search size={48} style={{ color: "var(--text-disabled)", opacity: 0.3, marginBottom: "16px" }} />
                    <p
                      style={{
                        color: "var(--text-muted)",
                        marginTop: "16px",
                      }}>
                      {tenantSearchTerm ? "No tenants match your search." : "No tenants available."}
                    </p>
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: "var(--space-md)" }}>
                    {filteredTenants.map((t) => {
                      const statusColor = t.status === "active" ? { bg: "var(--success-bg)", text: "var(--success)" } : { bg: "var(--danger-bg)", text: "var(--danger)" };
                      return (
                        <div
                          key={t.id}
                          style={{
                            padding: "var(--space-lg)",
                            border: "1px solid var(--border-primary)",
                            borderRadius: "var(--radius-md)",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "var(--space-lg)",
                            transition: "all var(--transition-fast)",
                            backgroundColor: "var(--surface)",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "var(--surface-hover)";
                            e.currentTarget.style.borderColor = "var(--border-accent)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "var(--surface)";
                            e.currentTarget.style.borderColor = "var(--border-primary)";
                          }}>
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontWeight: "600",
                                fontSize: "15px",
                                color: "var(--text-primary)",
                                marginBottom: "var(--space-xs)",
                              }}>
                              {t.name}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "var(--space-md)",
                                fontSize: "13px",
                              }}>
                              <code
                                style={{
                                  backgroundColor: "var(--surface-elevated)",
                                  padding: "2px 8px",
                                  borderRadius: "var(--radius-sm)",
                                  fontSize: "12px",
                                  color: "var(--text-secondary)",
                                }}>
                                {t.slug || "–"}
                              </code>
                              <span
                                style={{
                                  padding: "2px 8px",
                                  borderRadius: "var(--radius-sm)",
                                  fontSize: "11px",
                                  fontWeight: "600",
                                  backgroundColor: statusColor.bg,
                                  color: statusColor.text,
                                }}>
                                {t.status || "unknown"}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleSelectTenant(t)}
                            disabled={!t.slug}
                            style={{
                              padding: "var(--space-sm) var(--space-lg)",
                              backgroundColor: t.slug ? "var(--accent-primary)" : "var(--surface-elevated)",
                              color: t.slug ? "#fff" : "var(--text-disabled)",
                              border: "none",
                              borderRadius: "var(--radius-md)",
                              fontSize: "13px",
                              fontWeight: "600",
                              cursor: t.slug ? "pointer" : "not-allowed",
                              transition: "all var(--transition-fast)",
                            }}
                            onMouseEnter={(e) => {
                              if (t.slug) e.currentTarget.style.backgroundColor = "var(--accent-secondary)";
                            }}
                            onMouseLeave={(e) => {
                              if (t.slug) e.currentTarget.style.backgroundColor = "var(--accent-primary)";
                            }}>
                            Access
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div
                style={{
                  padding: "var(--space-lg) var(--space-xl)",
                  borderTop: "1px solid var(--border-primary)",
                  display: "flex",
                  justifyContent: "flex-end",
                }}>
                <button
                  onClick={() => setShowTenantPicker(false)}
                  style={{
                    padding: "var(--space-sm) var(--space-xl)",
                    backgroundColor: "var(--surface)",
                    color: "var(--text-secondary)",
                    border: "1px solid var(--border-primary)",
                    borderRadius: "var(--radius-md)",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: "pointer",
                    transition: "all var(--transition-fast)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "var(--surface-hover)";
                    e.currentTarget.style.borderColor = "var(--border-secondary)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "var(--surface)";
                    e.currentTarget.style.borderColor = "var(--border-primary)";
                  }}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      </div>
    </ErrorBoundary>
  );
}

function ActionCard({ icon: Icon, title, description, onClick }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        backgroundColor: "var(--surface)",
        border: isHovered ? "1px solid var(--border-accent)" : "1px solid var(--border-primary)",
        borderRadius: "var(--radius-lg)",
        padding: "var(--space-xl)",
        cursor: "pointer",
        transition: "all var(--transition-fast) var(--ease-out-quart)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-md)",
        boxShadow: isHovered ? "var(--shadow-md)" : "var(--shadow-sm)",
        transform: isHovered ? "translateY(-2px)" : "translateY(0)",
        position: "relative",
        overflow: "hidden",
      }}>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "4px",
          height: "100%",
          backgroundColor: "var(--accent-primary)",
          transform: isHovered ? "scaleY(1)" : "scaleY(0)",
          transformOrigin: "top",
          transition: "transform var(--transition-normal) var(--ease-out-quart)",
        }}
      />
      <Icon size={28} strokeWidth={2} style={{ color: "var(--accent-primary)" }} />
      <h3
        style={{
          fontSize: "16px",
          fontWeight: "600",
          color: "var(--text-primary)",
          margin: 0,
        }}>
        {title}
      </h3>
      <p
        style={{
          fontSize: "14px",
          color: "var(--text-secondary)",
          margin: 0,
          lineHeight: "1.5",
        }}>
        {description}
      </p>
    </div>
  );
}
